import React, { useState, useEffect } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { isEmpty } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { connect } from 'react-redux';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import TextArea from '../../../components/FormFields/TextArea';
import BobConfirmButton from './../../../components/BobConfirmButton';

import * as ArchitectActions from '../../../actions/architectActions';
import * as ApplicationMessageActions from '../../../actions/applicationMessageActions';
import * as BobActions from '../../../actions/bobActions';
import * as SiteActions from '../../../actions/siteActions';
import * as jobActions from '../../../actions/jobActions';
import * as uiActions from '../../../actions/uiActions';

import classes from '../CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    description:   Yup.string(),
    priority:      Yup.number().required(),
    issueType:     Yup.string().required(),
    clientContact: Yup.string(),
    client:        Yup.string().required(),
    equipment:     Yup.string().required(),
    site:          Yup.string().required(),
    target:        Yup.string(),
    faults:        Yup.array(),
    skillType:     Yup.string().required(),
    skillLevel:    Yup.string().required(),
});

const DEFAULT_FORM_VALUES = {
    description: '',
    priority:    0,
    issueType:   '',
    equipment:   '',
    client:      '',
    site:        '',
    target:      '',
    faults:      [],
    skillType:   '',
    skillLevel:  '',
};

const formattedPriorities = [
    {
        id:   2,
        name: 'High'
    },
    {
        id:   1,
        name: 'Medium'
    },
    {
        id:   0,
        name: 'Low'
    }
];

const CreateServiceRequestModal = ({
    activeNavCompany,
    close,
    scheduler,
    clearErrors,
    companyInfo,
    createServiceCall,
    currentUser,
    equipment,
    errors,
    entityRules,
    fetchClientSites,
    fetchEntityRules,
    fetchServiceManagementLookups,
    fetchServiceRequestListAfterCreate,
    isFromFaults,
    isLoading,
    issueTypes,
    navClients,
    open,
    sites,
    seniorityLevels,
    specialties,
    skillLevel,
    skillTypes,
    title,
    activeNavClient,
    activeNavSite,
    activeNavEquipment,
    toggleLoading,
    fetchEquipmentsBySiteId,
    onClose,
    ...props
}) => {
    const [formatedFaults, setFormatedFaults] = useState([]);
    const [clientSelected, setClientSelected] = useState(false);
    const [equipments, setEquipments] = useState([]);
    const [initialValues, setInitialValues] = useState({...DEFAULT_FORM_VALUES});

    useEffect(() => {
        const init = async () => {
            await fetchEntityRules(equipment.companyId, equipment.equipmentId);
        };

        if (equipment && equipment.clientId) init();
    }, [equipment]);

    useEffect(() => {
        async function init() {
            await Promise.all([
                fetchServiceManagementLookups(activeNavCompany.id, 'Jobissuetype'),
                fetchServiceManagementLookups(activeNavCompany.id, 'Skilllevel'),
                fetchServiceManagementLookups(activeNavCompany.id, 'Skilltype')
            ].map(p => p.catch(err => err)));
        }

        if (activeNavCompany && activeNavCompany.id) init();
    }, [activeNavCompany]);

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {

            let faultIds = formatedFaults.filter(f => formik.values.faults.includes(f.name)).map(fault => fault.id);
            let selectedIssueType = issueTypes.filter(f => formik.values.issueType === f.id);
            let priority = formattedPriorities.find(f => formik.values.priority === f.id);

            let payload = {
                'serviceRequestMeta': {
                    'skillTypeId':  [values.skillType],
                    'skilllevelId': values.skillLevel,
                    'equipmentId':  [values.equipment],
                    'faultId':      faultIds,
                    'systemId':     []
                },
                'summary':             '',
                'description':         values.description,
                'jobNumber':           '',
                'clientId':            values.client,
                'siteId':              values.site,
                'jobType':             'ServiceRequest',
                'jobStatus':           'Requested',
                'jobStatusReason':     'CreditHold',
                'jobStatusReasonText': 'Accepted',
                'jobIssueTypes':       selectedIssueType,
                'source':              'UI',
                'priority':            priority.name
            };

            let isCreated;

            if (!isLoading) {
                toggleLoading(true);
                isCreated = await createServiceCall(activeNavCompany.id, payload);
            }

            if (isCreated) {
                handleClose();
            } else {
                toggleLoading(false);
            }
        }
    });
    
    useEffect(() => {
        if (!isEmpty(entityRules)) {
            const payload = entityRules.map(rule => {
                return { id: rule.ruleId, name: rule.ruleName };
            });

            const formatted = payload;

            setFormatedFaults(formatted);
        }
    }, [entityRules]);

    useEffect(() => {
        const init = async () => {
            setClientSelected(true);
            let clientId = '';
            let siteId = '';
            let equipmentId = '';

            // activeNavClient object has values when they create service request from faults tab
            if (activeNavClient.id) {
                await fetchClientSites(activeNavClient.id);
                clientId = activeNavClient.id;
                siteId = activeNavSite.id;
            }
            
            // equipment object has values when they create service request from equipment tab
            if (equipment && equipment.clientId) {
                await fetchClientSites(equipment.clientId);
                clientId = equipment.clientId;
                siteId = equipment.siteId;
            }

            if (activeNavEquipment && activeNavEquipment.id) {
                equipmentId = activeNavEquipment.id;
            }

            // defaulting entity values
            setInitialValues({
                ...initialValues,
                client:    clientId,
                site:      siteId,
                equipment: equipmentId
            });
        };

        if (activeNavEquipment.id || (equipment && equipment.clientId)) init();
    }, [activeNavEquipment, equipment]);

    const handleClientChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('site', '', false);

        formik.setTouched({
            site: false
        });

        setClientSelected(true);

        toggleLoading(false);
        await fetchClientSites(value);

        formik.handleChange(event);
    };

    const handleSiteChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('equipment', '', false);

        formik.setTouched({
            equipment: false
        });
        
        const equipments = await fetchEquipmentsBySiteId(value);
        let equipmentList = [];
        if (equipments) {
            // eslint-disable-next-line array-callback-return
            equipments.map(item => {
                equipmentList.push(
                    {
                        id:   item.id,
                        name: item.name
                    }
                );
            });
        }
        setEquipments(equipmentList);
        toggleLoading(false);
        formik.handleChange(event);
    };

    const handleEquipmentChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('faults', [], false);

        formik.setTouched({
            faults: false
        });
        
        if (!scheduler) fetchEntityRules(activeNavCompany.id, value);
        toggleLoading(false);
        formik.handleChange(event);
    };

    const handleClose = () => {
        fetchServiceRequestListAfterCreate();
        toggleLoading(false);
        clearErrors();
        onClose();
    };
    
    return (
        <React.Fragment>
            <div className={classes.formColumn}>
                <div className={classes.createServiceRequestInputColumn}>
                    <div className={classes.inputs}>
                        {!scheduler && isFromFaults ? <div className={classes.ServiceRequestInfo}>
                            <p className={classes.firstElement}>Equipment</p>
                            <p>{!isEmpty(activeNavEquipment) ? activeNavEquipment.name : ''}{!isEmpty(equipment) ? equipment.equipmentName : ''}</p>
                        </div> : null}
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="client"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                Client<span className={classes.required}> *</span>
                            </InputLabel>
                            <Select
                                value={formik.values.client}
                                onChange={handleClientChange}
                                className={classes.select}
                                disabled={scheduler || !isFromFaults ? false : true}
                                inputProps={{
                                    name:   'client',
                                    id:     'client',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                {
                                    navClients.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.client &&
                             formik.errors.client && <FormHelperText className={classes.error}>{formik.errors.client}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="site"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                Site<span className={classes.required}> *</span>
                            </InputLabel>
                            <Select
                                value={formik.values.site}
                                onChange={handleSiteChange}
                                className={classes.select}
                                disabled={scheduler || !isFromFaults ? false : !clientSelected || true}
                                inputProps={{
                                    name:   'site',
                                    id:     'site',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                {
                                    sites.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                             formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                        {scheduler || !isFromFaults ? <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="equipment"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.equipment) && Boolean(formik.touched.equipment)}
                            >
                                Equipments<span className={classes.required}> *</span>
                            </InputLabel>
                            <Select
                                value={formik.values.equipment}
                                onChange={handleEquipmentChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'equipment',
                                    id:     'equipment',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.equipment)}
                            >
                                {
                                    equipments.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.equipment &&
                             formik.errors.equipment && <FormHelperText className={classes.error}>{formik.errors.equipment}</FormHelperText>}
                        </FormControl> : null}
                    </div>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="faults"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                            >
                                Faults
                            </InputLabel>
                            <Select
                                multiple
                                value={formik.values.faults}
                                id="faults"
                                input={<Input id="select-multiple-checkbox" />}
                                renderValue={selected => selected.join(', ')}
                                onChange={formik.handleChange}
                                name="faults"
                                className={`${classes.cssUnderline} ${classes.input}`}
                                inputProps={{
                                    name: 'faults',
                                    id:   'faults',
                                }}
                            >
                                {
                                    formatedFaults.map(f => f.name).map((type, index) => (
                                        <MenuItem
                                            className={classes.selectMenuItem}
                                            value={type}
                                            key={index}
                                        >
                                            <Checkbox
                                                color="primary"
                                                checked={formik.values.faults.indexOf(type) > -1}
                                            />

                                            <span>{type}</span>
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceRequest}>
                                <InputLabel
                                    htmlFor="issueType"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.issueType) && Boolean(formik.touched.issueType)}
                                >
                                    Issue Type<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.issueType}
                                    onChange={ e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'issueType',
                                        id:     'issueType',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.issueType) && Boolean(formik.touched.issueType)}
                                >
                                    {
                                        issueTypes.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.issueType &&
                                formik.errors.issueType && <FormHelperText className={classes.error}>{formik.errors.issueType}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceRequest}>
                                <InputLabel
                                    htmlFor="priority"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.priority) && Boolean(formik.touched.priority)}
                                >
                                    Priority<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.priority}
                                    onChange={ e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'priority',
                                        id:     'priority',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.priority) && Boolean(formik.touched.priority)}
                                >
                                    {
                                        formattedPriorities.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.priority &&
                                formik.errors.priority && <FormHelperText className={classes.error}>{formik.errors.priority}</FormHelperText>}
                            </FormControl>
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceRequest}>
                                <InputLabel
                                    htmlFor="skillType"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.skillType) && Boolean(formik.touched.skillType)}
                                >
                                    Skill Type<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.skillType}
                                    onChange={ e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'skillType',
                                        id:     'skillType',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.skillType) && Boolean(formik.touched.skillType)}
                                >
                                    {
                                        skillTypes.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.skillType &&
                                formik.errors.skillType && <FormHelperText className={classes.error}>{formik.errors.skillType}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceRequest}>
                                <InputLabel
                                    htmlFor="skillLevel"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.skillLevel) && Boolean(formik.touched.skillLevel)}
                                >
                                    Skill Level<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.skillLevel}
                                    onChange={ e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'skillLevel',
                                        id:     'skillLevel',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.skillLevel) && Boolean(formik.touched.skillLevel)}
                                >
                                    {
                                        skillLevel.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.skillLevel &&
                                formik.errors.skillLevel && <FormHelperText className={classes.error}>{formik.errors.skillLevel}</FormHelperText>}
                            </FormControl>
                        </div>
                        <FormControl className={classes.inputWrapper}>
                            <TextArea
                                name="description"
                                type="text"
                                multiline={true}
                                value={formik.values.description}
                                label="Description"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error: classes.cssErrorUnderline,
                                        root:  classes.summaryTextArea
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.description) && Boolean(formik.touched.description)}
                                helperText={Boolean(formik.touched.description) && formik.errors.description}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
            <React.Fragment>
                <Button
                    className={classes.Button}
                    onClick={onClose}
                    variant="contained"
                >
                    Cancel
                </Button>
                <BobConfirmButton
                    className={classes.ButtonWithMargin}
                    isLoading={isLoading}
                    disabled={formik.isSubmitting || isLoading}
                    label={'Create'}
                    onClick={formik.handleSubmit}
                />
            </React.Fragment>
        </React.Fragment>
    );
};

CreateServiceRequestModal.propTypes = {
    close:       func,
    companyInfo: object,
    currentUser: object,
    errors:      array,
    isLoading:   bool,
    open:        bool,
    rule:        object,
    title:       string,
    scheduler:   bool
};

const mapStateToProps = state => {
    return {
        activeNavCompany:   state.bob.activeNavCompany,
        companyInfo:        state.companyInfo,
        currentUser:        state.currentUser,
        errors:             state.messages.errors,
        entityRules:        state.bob.entityRules,
        isLoading:          state.ui.isLoading,
        issueTypes:         state.job.issueTypes,
        navClients:         state.bob.navClients,
        sites:              state.site.sites,
        targetNavCompany:   state.bob.targetNavCompany,
        activeNavClient:    state.bob.activeNavClient,
        activeNavSite:      state.bob.activeNavSite,
        activeNavEquipment: state.bob.activeNavEquipment,
        skillLevel:         state.job.skillLevel,
        skillTypes:         state.job.skillTypes
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...BobActions,
    ...SiteActions,
    ...jobActions,
    ...uiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateServiceRequestModal);
